import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import momentTz from 'moment-timezone'
import { api } from 'core/api/ConnectionManager'
import Button from 'blocks.simple/button/button'
import EditText from 'blocks.simple/editText/editText'
import translate from 'core/translate'
import LoaderLazy from 'blocks.app/loader/_lazy/loader__lazy'
import { cn } from 'ethcss'
import styles from './displays__datetime.jcss'
import { grid, item } from 'theme'
import { text as textStyles } from 'theme'
import { Typography } from 'atoms/Typography'
import { emitError } from 'features/appNotifications/AppNotifications.state'
import { displaysActions } from '../displays.state'
import helpers from 'core/helpers'
import { resolveLegacyTimezone } from 'core/helpers/timezone'
import Dropdown from 'blocks.simple/dropdown/dropdown'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import { Switch } from '../../../atoms/Switch'
import { LoadingText } from '../../../blocks.simple/LoadingText/LoadingText'
import DotTooltip from '../../../blocks.simple/dotTooltip/dotTooltip'

type DSState = {
    currentDate: string
    timezone: string
    id: number
}

interface IDisplaysDateTime {
    selectedItems: any[]
    emitError: (error: string) => void
    closeModal: () => void
    afterAction?: () => void
    timezones: Array<{
        id: string
        name: string
        gmtOffset: number
        timezoneAbbr: string
    }>
    lang: string
}

interface ITableData {
    id: number
    name: string
    isSelected: boolean
    currentDate: string
    currentTime: string
    timezoneString: string
    breadcrumbs: string
}

const DisplaysDateTime: React.FC<IDisplaysDateTime> = ({ selectedItems, closeModal, afterAction, timezones }) => {
    const [automaticallyDatetimeQ, setAutomaticallyDatetimeQ] = useState<boolean>(false)
    const [date, setDate] = useState(moment())
    const [time, setTime] = useState(moment().format('HH:mm'))
    const [timezone, setTimezone] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [nullTimezoneError, setNullTimezoneError] = useState(false)
    const [emptyListError, setEmptyListError] = useState(false)

    const [devices, setDevices] = useState<ITableData[]>([])

    const toggleDeviceSelection = useCallback((deviceId: number, deviceName: string) => {
        setDevices((prevDevices) =>
            prevDevices.map((device) =>
                device.id === deviceId && device.name === deviceName
                    ? { ...device, isSelected: !device.isSelected }
                    : device
            )
        )
    }, [])

    const toggleAllDevices = useCallback(() => {
        const allSelected = devices.every((device) => device.isSelected)
        setDevices((prevDevices) =>
            prevDevices.map((device) => ({
                ...device,
                isSelected: !allSelected,
            }))
        )
    }, [devices])

    useEffect(() => {
        if (devices.length > 0) {
            setEmptyListError(false)
        }
    }, [devices])

    useEffect(() => {
        setNullTimezoneError(false)
    }, [timezone])

    const selectedDevicesCount = devices.filter((device) => device.isSelected).length

    const prepareApiPayload = useCallback(() => {
        const payload: { groupId?: number[]; digitalSignageId?: number[] } = {}

        const digitalSignageId = selectedItems.filter((item) => item.type === 'digitalSignage').map((item) => item.id)

        if (digitalSignageId.length > 0) {
            payload.digitalSignageId = digitalSignageId
        }

        const groupId = selectedItems.filter((item) => item.type === 'group').map((item) => item.id)

        if (groupId.length > 0) {
            payload.groupId = groupId
        }
        if (!payload.digitalSignageId && !payload.groupId) {
            return
        }

        return payload
    }, [selectedItems])

    const onFDsGetState = useCallback((dsState: DSState) => {
        setIsLoading(false)

        if (dsState.currentDate) {
            let newDate = moment(dsState.currentDate)
            const current = moment.utc(dsState.currentDate)
            let newConvertedTime = helpers.getFormattedLocalDate(dsState.currentDate, 'HH:mm')

            if (dsState.timezone) {
                newDate = momentTz(current).tz(resolveLegacyTimezone(dsState.timezone) || dsState.timezone)
                newConvertedTime = newDate.format('HH:mm')
            }

            setDevices((prevDevices) =>
                prevDevices.map((device) => {
                    if (device.id === dsState.id) {
                        return {
                            ...device,
                            currentDate: newDate.format('DD.MM.YYYY'),
                            currentTime: newConvertedTime ?? '-',
                        }
                    } else {
                        return device
                    }
                })
            )
        }
    }, [])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver('fDsGetState', onFDsGetState, listenersId)

        const payload = prepareApiPayload()
        if (!payload) {
            return () => {
                listenersId.forEach((id) => api.removeObserver(id))
            }
        }

        api.send('getDateTimeFromMultipleDevices', payload, { showEmitError: true }).then((res: any) => {
            if (res && res.length) {
                const rowData: ITableData[] = res.map((item: any) => {
                    return {
                        id: item.id,
                        name: item.name,
                        isSelected: true,
                        currentDate: item.currentDate ? moment(item.currentDate).format('DD.MM.YYYY') : '-',
                        currentTime: item.currentTime ? moment(item.currentTime).format('HH:mm') : '-',
                        timezoneString: `(UTC ${item.timezoneOffset > 0 ? '+' : ''}${item.timezoneOffset / 60}) ${
                            item.timezone
                        }`,
                        breadcrumbs: item.breadcrumbs || '-',
                    }
                })
                setDevices(rowData)
            }
        })

        setIsLoading(true)
        const getDsStateTimer = setTimeout(() => {
            setIsLoading(false)
        }, 10000)

        return () => {
            clearTimeout(getDsStateTimer)
            listenersId.forEach((id) => api.removeObserver(id))
        }
    }, [])

    const save = () => {
        if (!timezone) {
            setNullTimezoneError(true)
            return
        }

        if (devices.every((item) => !item.isSelected)) {
            setEmptyListError(true)
            return
        }

        const selectedDateTime = moment.utc(`${date.format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD HH:mm')

        const selectedTimezone = timezones.find((tz) => tz.id === timezone)

        let dateTimeInUTC = selectedDateTime
        if (selectedTimezone) {
            const offsetInMinutes = selectedTimezone.gmtOffset / 60
            dateTimeInUTC = selectedDateTime.clone().subtract(offsetInMinutes, 'minutes')
        }

        const payload = {
            digitalSignageId: devices.filter((device) => device.isSelected).map((device) => device.id),
            newDate: dateTimeInUTC.toISOString(),
            timezone: selectedTimezone ? timezone : momentTz.tz.guess(),
            automaticallyDatetimeQ,
        }

        api.send('setDigitalSignageDate', payload).then((res) => {
            closeModal()
            afterAction && afterAction()
        })
    }

    return (
        <div className={styles.modalWrapper}>
            <div className={styles.modalInner}>
                <div className={styles.contentWrapper}>
                    <Typography className={styles.title} type="title">
                        {translate('ds_date')}
                    </Typography>
                    <div className={styles.description}>{translate('setDateAndTime')}</div>

                    <div>
                        <div className={styles.autoAssetLabel}>{translate('setDateAndTimeAutomatically')}</div>
                        <Switch
                            onChange={() => {
                                setAutomaticallyDatetimeQ(!automaticallyDatetimeQ)
                            }}
                            checked={automaticallyDatetimeQ}
                        />
                    </div>
                    <div className={styles.content}>
                        <div className={styles.displaysDatetime__inputWrapper_type_date}>
                            <EditText
                                className={cn(textStyles.left)}
                                type="date"
                                dateValue={date}
                                onChange={(newDate: string) => setDate(moment(newDate))}
                                label={translate('date')}
                            />
                        </div>
                        <div className={styles.displaysDatetime__inputWrapper_type_time}>
                            <EditText
                                className={cn(textStyles.left)}
                                label={translate('time')}
                                mask="99:99"
                                type="tel"
                                placeholder="__:__"
                                value={time}
                                onChange={(newTime: string) => setTime(newTime)}
                            />
                        </div>
                        <div className={styles.displaysDatetime__inputWrapper_type_timezone}>
                            <Dropdown
                                id={'timezones'}
                                label={translate('timezone')}
                                value={timezone}
                                options={timezones}
                                placeholder={translate('notSelected')}
                                searchable
                                centered
                                wrapperWidth
                                onChange={(selected) => {
                                    setTimezone(selected.id)
                                }}
                                labelClassName={cn(item.ellipsis)}
                            />
                            <div
                                className={cn(
                                    grid.mt_micro,
                                    textStyles.centerBlock,
                                    nullTimezoneError ? '' : item.hidden,
                                    styles.errorText
                                )}
                            >
                                {translate('fieldIsRequired')}
                            </div>
                        </div>
                    </div>
                    <div className={styles.loaderWrapper}>
                        <LoadingText
                            isLoading={isLoading}
                            text={translate('getClientData')}
                            className={styles.loader}
                        />
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <div className={styles.devicesTable}>
                            <div className={styles.tableHeader}>
                                <div className={styles.headerCell}>
                                    <input
                                        type="checkbox"
                                        className={styles.checkbox}
                                        checked={devices.length > 0 && devices.every((device) => device.isSelected)}
                                        onChange={toggleAllDevices}
                                    />
                                </div>
                                <div className={cn(styles.tableCell, styles.desktopOnly)}>{translate('id')}</div>
                                <div className={styles.headerCell}>{`${translate(
                                    'selectedDevices'
                                )}: ${selectedDevicesCount}`}</div>
                                <div className={styles.headerCell}>{translate('currentTime')}</div>
                                <div className={styles.headerCell}>{translate('currentDate')}</div>
                                <div className={styles.headerCell}>{translate('timezone')}</div>
                                <div className={cn(styles.tableCell, styles.desktopOnly)}>{translate('location')}</div>
                            </div>
                            <div className={styles.tableList}>
                                {devices.map((device, index) => (
                                    <div key={`${device.id}-${device.name}-${index}`} className={styles.tableRow}>
                                        <div className={styles.tableCell}>
                                            <Checkbox
                                                checked={device.isSelected}
                                                onClick={() => toggleDeviceSelection(device.id, device.name)}
                                            />
                                        </div>
                                        <div
                                            className={cn(styles.tableCell, styles.desktopOnly)}
                                            data-label="Идентификатор:"
                                        >
                                            <span className={styles.idText}>{device.id}</span>
                                        </div>
                                        <div className={styles.tableCell} data-label="Выбранные устройства:">
                                            <DotTooltip tooltip={{ html: device.name }}>
                                                <div className={item.ellipsis}>{device.name}</div>
                                            </DotTooltip>
                                        </div>
                                        <div className={styles.tableCell} data-label="Текущее время:">
                                            {device.currentTime}
                                        </div>
                                        <div className={styles.tableCell} data-label="Текущая дата:">
                                            {device.currentDate}
                                        </div>
                                        <div className={styles.tableCell} data-label="Тайм-зона:">
                                            <DotTooltip tooltip={{ html: device.timezoneString }}>
                                                <div className={item.ellipsis}>{device.timezoneString}</div>
                                            </DotTooltip>
                                        </div>
                                        <div
                                            className={cn(styles.tableCell, styles.desktopOnly)}
                                            data-label="Где находится:"
                                        >
                                            {device.breadcrumbs}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {emptyListError && (
                            <div
                                className={cn(
                                    grid.mt_micro,
                                    textStyles.centerBlock,
                                    emptyListError ? '' : item.hidden,
                                    styles.errorText
                                )}
                            >
                                {translate('emptyDisplayListError')}
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.buttons}>
                    <div className={grid.row}>
                        <Button
                            mod={'withBorder'}
                            className={cn(grid.mr_mini)}
                            textSize={'mini'}
                            indentSize={'mini'}
                            rounded={'full_normal'}
                            onClick={closeModal}
                        >
                            {translate('cancel')}
                        </Button>
                        <Button
                            mod={'fill'}
                            textSize={'mini'}
                            indentSize={'mini'}
                            rounded={'full_normal'}
                            onClick={save}
                        >
                            {translate('apply')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        emitError: (err: string) => dispatch(emitError(err)),
        closeModal: () => dispatch(displaysActions.closeModal()),
    }
}

const mapStateToProps = (state: {
    user: {
        data: {
            settings: {
                locale: string
            }
        }
    }
    app: {
        timezones: Array<{
            id: string
            name: string
            gmtOffset: number
            timezoneAbbr: string
        }>
    }
}) => {
    return {
        lang: state.user.data.settings.locale,
        timezones: state.app.timezones,
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(DisplaysDateTime)
